import { getEmployeeExperienceBaseUrl } from 'modules/api/employee-experience'
import { setShpLastBridgedEmployee } from 'helpers/shopHealthSession'

export type BridgeTransferParams = {
  idToken: string
  employeePublicKey: string
  isImpersonated: boolean
  onError: (message: string) => void
  groupId?: string
  target?: string
}

/**
 * Performs bridge transfer to employee experience shop for health page
 * @param params - Bridge transfer parameters
 */
export const handleBridgeTransfer = async ({
  idToken,
  employeePublicKey,
  isImpersonated,
  onError,
  groupId,
  target,
}: BridgeTransferParams): Promise<void> => {
  if (!idToken) {
    console.error('Not authenticated. Please log in again.')
    onError('Not authenticated. Please log in again.')
    return
  }

  try {
    const baseUrl = getEmployeeExperienceBaseUrl()
    const url = `${baseUrl}/api/v1/employee-experience/auth/bridge-login`

    // When impersonating, include the target employee's public key in the request body
    // so the backend creates a token for the impersonated user with impersonation claims

    const body = isImpersonated ? { targetEmployeePublicKey: employeePublicKey } : {}
    if (target) {
      body['target'] = target
    }
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      throw new Error(`Request failed with status ${response.status}: ${errorText}`)
    }

    const data = await response.json()
    const redirectUrl = data.redirectUrl

    if (!redirectUrl) {
      throw new Error('Redirect URL not found in response')
    }

    setShpLastBridgedEmployee(employeePublicKey)

    // Append groupId to the redirect URL as a query parameter if provided
    const urlWithGroupId = new URL(redirectUrl)

    /**
     * For QLE, we need to set the access token in the query paramsso the backend can use it to authenticate the user
     */
    if (target && target === 'qle') {
      const accessToken = JSON.parse(localStorage.getItem('admin-token') || '{}')?.idToken
      if (accessToken) {
        urlWithGroupId.searchParams.set('accessToken', accessToken)
      }
      // For QLE, we need to set the groupNo in the query params so the backend can use it to load the QLE group
      if (groupId) {
        urlWithGroupId.searchParams.set('groupNo', groupId)
      }
    } else {
      if (groupId) {
        urlWithGroupId.searchParams.set('groupId', groupId)
      }
    }

    window.location.href = urlWithGroupId.toString()
  } catch (err) {
    console.error('Failed to transfer to employee experience:', err)
    const errorMessage =
      err instanceof Error ? err.message : 'Failed to transfer. Please try again.'
    onError(errorMessage)
  }
}
